import { PrimaryBigButton } from 'components/ReusableComponents/Button/BigButtons'
import { CheckIcon } from 'components/ReusableComponents/Icons/Check'
import EmailIcon from 'components/ReusableComponents/Icons/Email'
import PhoneIcon from 'components/ReusableComponents/Icons/Phone'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import PdfViewerDialog from 'components/ReusableComponents/PdfViewerDialog'
import HotelSetupFormGroup from 'features/HotelSetup/FormGroup'
import RadioLabel from 'features/HotelSetup/RadioLabel'
import { canadaTimeZones, UStimeZones } from 'features/ManageUserDialog'
import {
  userPositionNames,
  userPositions
} from 'features/ManageUserDialog/userTitleTypes'
import useWhoIs from 'hooks/useWhois'
import html2canvas from 'html2canvas'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import contractApi from 'services/api/contracts'
import { emailRegex, formatPhoneNumber, phoneValRegex } from 'utils/helpers'

import styles from './Addendum.module.scss'

const DiscountPill = ({ monthlyRate, rate }) => {
  const ratio = (monthlyRate - rate) * 100
  const discount = Math.round(ratio / monthlyRate)

  if (discount > 0) {
    return <div className={styles.discountPill}>{discount}% off</div>
  }

  return null
}

const HotelSetupAddendum = ({ onStepComplete }) => {
  const isFinalized = false
  const [isTermsChecked, setIsTermsChecked] = useState(false)

  const [contractPath, setContractPath] = useState()
  const [merchantName, setMerchantName] = useState()
  const [hotelName, setHotelName] = useState()
  const [supportEmail, setSupportEmail] = useState()
  const [supportPhone, setSupportPhone] = useState()
  const [userName, setUserName] = useState()
  const [userEmail, setUserEmail] = useState()
  const [userPhone, setUserPhone] = useState()
  const [userPosition, setUserPosition] = useState()

  const whoIsData = useWhoIs()

  const [tzTimeZoneName, setTzTimeZoneName] = useState()

  const [priceOption, setpriceOption] = useState()
  const [initialPriceOption, setInitialPriceOption] = useState()
  const [monthlyRate, setMonthlyRate] = useState()
  const [quarterlyRate, setQuarterlyRate] = useState()
  const [annualRate, setAnnualRate] = useState()
  const [serviceStartDate, setServiceStartDate] = useState()

  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitLoading, setIsSubmitLoading] = useState(false)

  function openPdfDialog(e) {
    e.preventDefault()
    setIsPdfDialogOpen(true)
  }

  useEffect(() => {
    setIsLoading(true)

    contractApi.addendum.get().then(res => {
      setContractPath(res.contractPath)
      setMerchantName(res.hotel.merchantName)
      setHotelName(res.hotel.name)
      setSupportEmail(res.hotel.supportEmail)
      setSupportPhone(res.hotel.supportPhone)
      setUserName(res.user.name)
      setUserEmail(res.user.email)
      setUserPhone(res.user.phone)
      setUserPosition(
        res.user.position === userPositions.Unknown ? null : res.user.position
      )
      setTzTimeZoneName(res.user.tzTimeZoneName)
      setpriceOption(res.prepayRateOptions.activePaymentPeriod)
      setInitialPriceOption(res.prepayRateOptions.activePaymentPeriod)
      setMonthlyRate(res.prepayRateOptions.monthlyRate)
      setQuarterlyRate(res.prepayRateOptions.quarterlyRate)
      setAnnualRate(res.prepayRateOptions.annualRate)
      setServiceStartDate(res.prepayRateOptions.serviceStartDate)

      setIsLoading(false)
    })
  }, [])

  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState()

  function handleChangePhoneInput(e) {
    setSupportPhone(formatPhoneNumber(e.target.value))
  }

  const { addToast } = useToasts()

  async function handleContinue() {
    const isEmailValid = new RegExp(emailRegex).test(supportEmail)

    const isPhoneValid = new RegExp(phoneValRegex).test(supportPhone)

    if (!userPosition) {
      addToast('Please select a role', {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    if (!isEmailValid) {
      addToast('Enter valid hotel support email', {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    if (!isPhoneValid) {
      addToast('Enter valid hotel support phone', {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    if (!isTermsChecked) {
      addToast('Please agree to terms', {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    setIsSubmitLoading(true)

    try {
      const canvas = await html2canvas(document.querySelector('#root'), {
        onclone: document => {
          document.getElementById('deviceInfo').style.display = 'flex'
          document.querySelector('noscript').style.display = 'none'
        }
      })
      const screenShot = canvas.toDataURL('image/png', 1)

      const submitObj = {
        supportEmail,
        supportPhone: supportPhone.replace(/\D/g, ''),
        position: userPosition,
        tzTimeZoneName,
        paymentPeriod: priceOption,
        screenShot
      }

      await contractApi.addendum.post(submitObj)

      onStepComplete()
    } catch (err) {
      addToast(err.message, {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setIsSubmitLoading(false)
    }
  }

  if (isLoading) {
    return <LoadingIndicator />
  }

  return (
    <div className={styles.container} id="hotel-setup-addendum">
      {isPdfDialogOpen && (
        <PdfViewerDialog
          onClose={() => setIsPdfDialogOpen(false)}
          pdfUrl={contractPath}
          title={`${merchantName} contract`}
        />
      )}
      <div id="hotel-setup-addendum-body" className={styles.sections}>
        <HotelSetupFormGroup title="Your information" disabled={isSubmitLoading}>
          <div className={styles.staticUserInfo}>
            <p className={styles.userName}>{userName}</p>
            <div className={styles.namePhoneHolder}>
              <div className={styles.userEmail}>
                <EmailIcon className={styles.emailIcon} /> {userEmail}
              </div>
              {userPhone && (
                <div className={styles.userPhone}>
                  <PhoneIcon className={styles.phoneIcon} />
                  {formatPhoneNumber(userPhone)}
                </div>
              )}
            </div>
          </div>
          <div className={styles.doubleInputRow}>
            <Dropdown
              defaultVal="Select position"
              options={{
                Corporate: userPositionNames.Corporate,
                GeneralManager: userPositionNames.GeneralManager,
                AGM: userPositionNames.AGM,
                DOS: userPositionNames.DOS,
                Accounting: userPositionNames.Accounting
              }}
              label="Role"
              value={userPosition}
              onChange={e => setUserPosition(e.target.value)}
              isSortedAlphabetically={false}
            />
            <Dropdown
              defaultVal="Select timezone"
              options={{ ...UStimeZones, ...canadaTimeZones }}
              isSortedAlphabetically={false}
              label="Timezone"
              value={tzTimeZoneName}
              onChange={e => setTzTimeZoneName(e.target.value)}
            />
          </div>
        </HotelSetupFormGroup>
        <HotelSetupFormGroup title="Subscription" disabled={isSubmitLoading}>
          {!!serviceStartDate && (
            <p className={styles.subscriptionDescription}>
              Your service is set to begin on{' '}
              {moment(serviceStartDate).format('MMM DD, YYYY')}. You will receive your
              invoice once the hotel setup process is finalized.
            </p>
          )}

          <div className={styles.radioHolder}>
            <RadioLabel
              name="Annual Subscription"
              labelText="Annual Subscription"
              checked={priceOption === 'Annual'}
              onChange={() => setpriceOption('Annual')}
            >
              <DiscountPill monthlyRate={monthlyRate} rate={annualRate} />
              <p className={styles.price}>
                <span className={styles.priceItself}>${annualRate.toFixed(2)}</span>
                <span className={styles.pricePeriod}>/ mo</span>
              </p>
            </RadioLabel>
            {initialPriceOption !== 'Annual' && (
              <RadioLabel
                name="Quarterly Subscription"
                labelText="Quarterly Subscription"
                checked={priceOption === 'Quarterly'}
                onChange={() => setpriceOption('Quarterly')}
              >
                <DiscountPill monthlyRate={monthlyRate} rate={quarterlyRate} />
                <p className={styles.price}>
                  <span className={styles.priceItself}>${quarterlyRate.toFixed(2)}</span>
                  <span className={styles.pricePeriod}>/ mo</span>
                </p>
              </RadioLabel>
            )}
            {initialPriceOption === 'Monthly' && (
              <RadioLabel
                name="Monthly Subscription"
                labelText="Monthly Subscription"
                checked={priceOption === 'Monthly'}
                onChange={() => setpriceOption('Monthly')}
              >
                <p className={styles.price}>
                  <span className={styles.priceItself}>${monthlyRate.toFixed(2)}</span>
                  <span className={styles.pricePeriod}>/ mo</span>
                </p>
              </RadioLabel>
            )}
          </div>
        </HotelSetupFormGroup>
        <HotelSetupFormGroup title="Support Contact" disabled={isSubmitLoading}>
          <p className={styles.subscriptionDescription}>
            Support contact information is required for providing effective support, legal
            compliance, and campaign registry.
          </p>
          <div className={styles.doubleInputRow}>
            <TextInput
              label="Support email"
              value={supportEmail}
              onChangeValue={setSupportEmail}
            />
            <TextInput
              label="Support phone"
              value={supportPhone}
              onChange={handleChangePhoneInput}
            />
          </div>
        </HotelSetupFormGroup>
        <HotelSetupFormGroup title="Addendum" disabled={isSubmitLoading}>
          <div className={styles.termsHolder}>
            <label className={styles.termsLabel}>
              {isFinalized ? (
                <CheckIcon />
              ) : (
                <input
                  type="checkbox"
                  checked={isTermsChecked}
                  onChange={e => setIsTermsChecked(e.target.checked)}
                ></input>
              )}
              <p className={styles.termsText}>
                I, {userName}, hereby agree and affirm that I have the authority to
                associate {hotelName} with {/* eslint-disable-next-line */}
                <a onClick={openPdfDialog}>
                  the contracts terms and conditions set forth by {merchantName} KSL and
                  SwayPay.
                </a>
              </p>
            </label>
            <div className={styles.deviceInfo} id="deviceInfo">
              <div>
                <p>Submitted by:</p>
                <p>{userName}</p>
                <p>{moment().format('MMMM Do YYYY, h:mm:ss a')}</p>
              </div>
              <div className={styles.whoIsData}>
                <p>
                  {whoIsData.isp} - IP: {whoIsData.ip}
                </p>
                <p>
                  {whoIsData.os} - {whoIsData.browserName} {whoIsData.browserVersion}
                </p>
                <p>
                  {whoIsData.city}, {whoIsData.country}
                </p>
              </div>
            </div>
          </div>
        </HotelSetupFormGroup>
      </div>
      <div className={styles.bottomBar}>
        <PrimaryBigButton disabled={isSubmitLoading} onClick={handleContinue}>
          {isSubmitLoading ? 'Submitting...' : 'Continue →'}
        </PrimaryBigButton>
      </div>
    </div>
  )
}

export default HotelSetupAddendum
